import { useCallback, useEffect, useRef, useState } from 'react';

import type { DashboardOverview, DashboardPeriod } from '../types';
import { sameDashboardOverview } from '../utils/overviewEquals';

type UseDashboardOverviewResult = {
  data: DashboardOverview | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  lastUpdatedAt: number | null;
  refetch: () => void;
};

const DASHBOARD_AUTO_REFRESH_MS = 30_000;

function isValidOverview(json: unknown): json is DashboardOverview {
  if (!json || typeof json !== 'object') return false;
  const o = json as Record<string, unknown>;
  const kpis = o.kpis as Record<string, unknown> | undefined;
  const chart = o.chart as Record<string, unknown> | undefined;
  return Boolean(
    kpis?.umsatz &&
      kpis?.anfragen &&
      kpis?.emails &&
      // angeboteVersendet ist neu und optional: Frontend und Backend werden getrennt ausgerollt.
      chart?.points &&
      o.greeting &&
      o.kpiComparison,
  );
}

export function useDashboardOverview(period: DashboardPeriod): UseDashboardOverviewResult {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const hasDataRef = useRef(false);
  const inFlightRef = useRef(false);
  const pendingRef = useRef<{ force: boolean } | null>(null);
  const forceNextRef = useRef(false);

  const scheduleFetch = useCallback((options?: { force?: boolean }) => {
    const force = options?.force === true;
    if (inFlightRef.current) {
      const pending = pendingRef.current;
      pendingRef.current = { force: force || pending?.force === true };
      return;
    }
    forceNextRef.current = force;
    setTick((n) => n + 1);
  }, []);

  const refetch = useCallback(() => scheduleFetch({ force: true }), [scheduleFetch]);

  useEffect(() => {
    let cancelled = false;
    inFlightRef.current = true;
    setError(null);
    if (hasDataRef.current) setRefreshing(true);
    else setLoading(true);

    const forceRefresh = forceNextRef.current;
    forceNextRef.current = false;
    const refreshParam = forceRefresh ? '&refresh=1' : '';
    fetch(`/api/dashboard/overview?period=${encodeURIComponent(period)}${refreshParam}`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? 'Dashboard konnte nicht geladen werden.');
        }
        return res.json();
      })
      .then((json: unknown) => {
        if (cancelled) return;
        if (!isValidOverview(json)) {
          throw new Error('Unerwartete Antwort vom Server. Bitte den Dev-Server neu starten.');
        }
        hasDataRef.current = true;
        setData((prev) => (prev && sameDashboardOverview(prev, json) ? prev : json));
        setLastUpdatedAt(Date.now());
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Dashboard konnte nicht geladen werden.');
      })
      .finally(() => {
        if (cancelled) return;
        inFlightRef.current = false;
        setLoading(false);
        setRefreshing(false);
        const pending = pendingRef.current;
        if (pending) {
          pendingRef.current = null;
          forceNextRef.current = pending.force;
          setTick((n) => n + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tick, period]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && hasDataRef.current) {
        scheduleFetch({ force: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [scheduleFetch]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || !hasDataRef.current) return;
      scheduleFetch();
    }, DASHBOARD_AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [scheduleFetch]);

  return { data, loading, refreshing, error, lastUpdatedAt, refetch };
}
